import React from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import OutlinedButton from '../Buttons/OutlinedButton';
import classes from './styles.module.scss';

export default function TrainingPathPlaylistSidebar({
  trainingPathName,
  content = [],
  currentMeetingId,
  trainingPathAssignmentId,
  isLoading = false,
  isCollapsed = false,
}) {
  const navigate = useNavigate();
  const isMobile = useCheckMobileScreen();

  const handleVideoClick = async (meetingId) => {
    if (!meetingId) return;

    let query = trainingPathAssignmentId
      ? `?trainingPathAssignmentId=${trainingPathAssignmentId}`
      : '';

    // If we have training path assignment, check for resume position
    if (trainingPathAssignmentId) {
      try {
        // Import the service dynamically to avoid circular imports
        const { default: TrainingPathService } = await import('../../services/TrainingPathService');

        const progressData = await TrainingPathService.getSectionProgress(
          trainingPathAssignmentId,
          meetingId
        );

        // Find the most recently updated section (last played)
        if (progressData && progressData.length > 0) {
          const lastPlayedSection = progressData[0]; // Already sorted by updatedAt DESC

          if (lastPlayedSection.sectionId) {
            query += `&resumeSection=${lastPlayedSection.sectionId}`;
          }
        }
      } catch (error) {
        // Continue without resume if there's an error
      }
    }

    navigate(`/my-videos/${meetingId}${query}`);
  };

  const handleExitTraining = () => {
    // Navigate to current video without training path assignment ID
    navigate(`/my-videos/${currentMeetingId}`);
  };

  // Convert both IDs to strings for comparison to handle type mismatches
  const currentMeetingIdStr = String(currentMeetingId);
  const hasContent = content.length > 0;
  const furthestCompletedIndex = content.reduce((maxIdx, video, idx) => {
    const progress = Number.isFinite(video.progressPercent)
      ? video.progressPercent
      : 0;
    if (progress >= 95) {
      return Math.max(maxIdx, idx);
    }
    return maxIdx;
  }, -1);
  const allowedIndex = furthestCompletedIndex + 1;

  return (
    <div className={classNames(classes.sidebar, { [classes.collapsed]: isCollapsed, [classes.mobile]: isMobile })}>
      <div className={classes.content}>
          <div className={classes.header}>
            <div className={classes.headerTop}>
              <h3 className={classes.title}>{trainingPathName || 'Training Path'}</h3>
              <OutlinedButton onClick={(e) => {
                e.stopPropagation();
                handleExitTraining();
              }} className={classes.exitButton}>
                Exit Training
              </OutlinedButton>
            </div>
            <p className={classes.subtitle}>Training Playlist</p>
          </div>

          <div className={classes.videoList}>
            {(() => {
              if (isLoading) {
                return (
                  <div className={classes.emptyState}>
                    <p>Loading training path content...</p>
                  </div>
                );
              }

              if (!hasContent) {
                return (
                  <div className={classes.emptyState}>
                    <p>No videos in this training path.</p>
                  </div>
                );
              }

              return content.map((video, index) => {
                const isCurrentVideo = String(video.id) === currentMeetingIdStr;
                const isTrainingView = Boolean(trainingPathAssignmentId);

                const progress = Number.isFinite(video.progressPercent)
                  ? video.progressPercent
                  : 0;
                const isCompleted = progress >= 95;

                const isFutureUnseen =
                  isTrainingView &&
                  index > allowedIndex &&
                  !isCompleted;

                const isClickable = !isTrainingView || !isFutureUnseen;
                const videoNumber = index + 1;
                return (
                  <div
                    key={video.id}
                    className={classNames(classes.videoItem, {
                      [classes.currentVideo]: isCurrentVideo,
                      [classes.disabled]: isFutureUnseen,
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isClickable) {
                        handleVideoClick(video.id);
                      }
                    }}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : -1}
                    onKeyDown={(e) => {
                      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleVideoClick(video.id);
                      }
                    }}
                  >
                    <span className={classes.videoNumber}>{videoNumber}</span>
                    {!isCollapsed && (
                      <div className={classes.videoContent}>
                    <span className={classes.videoTitle}>
                      {video.title || 'Untitled Video'}
                    </span>
                        <div className={classes.progressContainer}>
                          <div className={classes.progressBar}>
                            <div
                              className={classes.progressFill}
                              style={{ width: `${video.progressPercent || 0}%` }}
                            />
                          </div>
                          <span className={classes.progressText}>
                            {video.progressPercent || 0}%
                          </span>
                        </div>
                      </div>
                    )}
                    {isCollapsed && (
                      <div className={classes.collapsedVideoContent}>
                        <div className={classes.progressIndicator}>
                          <div className={classes.collapsedProgressBar}>
                            <div
                              className={classes.progressFill}
                              style={{ width: `${video.progressPercent || 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
    </div>
  );
}

